import React, {
  useState,
  useContext,
  createContext,
  useCallback,
  useMemo,
  memo,
} from "react";
import AppLayout from "@/Layouts/AppLayout";
import { useTitle } from "@/Utilities/Hooks";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import LayoutIcon from "@/Components/LayoutIcon";
import { IconGlobe } from "@/Icons";
import { Tab, Tabs } from "@/Components/Tabs";
import { Dropdown } from "@/Components";
import { Input } from "@/Components/Form";
import { Inertia } from "@inertiajs/inertia";
import AgencyRow from "@/Pages/Superuser/Agencies/Partials/AgencyRow";
import queryString from "query-string";

const SearchContext = createContext();

const Filter = memo(() => {
  const { search, setSearch, sort, handleSort, sortOptions } =
    useContext(SearchContext);

  const handleSearchChange = useCallback(
    (e) => {
      setSearch(e.target.value);
    },
    [setSearch],
  );

  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div className="w-full md:w-1/2">
        <Input
          className="w-full"
          name="Search"
          placeholder="Search"
          type="search"
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      <Dropdown
        options={sortOptions}
        title="Sort on"
        placeholder={
          sortOptions.find((o) => o.value === sort)?.label || "Name A-Ö"
        }
        onSelect={handleSort}
      />
    </div>
  );
});

const OrganizationGrid = memo(({ organizations, emptyState = true }) => {
  const { search, sort } = useContext(SearchContext);

  const sortedOrganizations = useMemo(() => {
    return organizations
      .filter((organization) =>
        search
          ? organization.name.toLowerCase().includes(search.toLowerCase())
          : true,
      )
      .sort((a, b) => {
        const [sortKey, sortDirection] = sort.split(".");
        if (a[sortKey] < b[sortKey]) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (a[sortKey] > b[sortKey]) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      });
  }, [organizations, search, sort]);

  if (emptyState && sortedOrganizations.length === 0) {
    return (
      <div className="mt-10 flex w-full flex-col items-center justify-center gap-1 p-4 text-center">
        <IconGlobe size={50} className="mb-2 text-typo-inactive" />
        <p className="font-bold">You have no global accounts</p>
        <p className="mb-4 text-typo-inactive">
          To add a global account, click on the "New global account" button
          above
        </p>
      </div>
    );
  }

  return (
    <>
      {sortedOrganizations.map((organization) => (
        <AgencyRow key={organization.id} organization={organization} />
      ))}
    </>
  );
});

export default function Agencies({ organizations }) {
  useTitle("Global accounts");

  const currentSort =
    queryString.parse(window.location.search).sort || "name.asc";

  const [sort, setSort] = useState(currentSort);
  const [search, setSearch] = useState("");

  const handleSort = useCallback((value) => {
    setSort(value);
    const newSearch = queryString.stringify({
      ...queryString.parse(window.location.search),
      sort: value,
    });
    Inertia.visit(`${window.location.pathname}?${newSearch}`);
  }, []);

  let tabs = ["Active", "Inactive"];

  let activeOrganizations = organizations.data.filter(
    (organization) => !organization.locked_at,
  );
  let inactivatedOrganizations = organizations.data.filter(
    (organization) => organization.locked_at,
  );

  const sortOptions = [
    { value: "name.asc", label: "Name A-Ö" },
    { value: "name.desc", label: "Name Ö-A" },
  ];

  return (
    <SearchContext.Provider
      value={{ search, setSearch, sort, handleSort, sortOptions }}
    >
      <Tabs items={tabs}>
        <Tab>
          <Filter />
          <OrganizationGrid organizations={activeOrganizations} />
        </Tab>
        <Tab>
          <Filter />
          <OrganizationGrid
            organizations={inactivatedOrganizations}
            emptyState={false}
          />
        </Tab>
      </Tabs>
    </SearchContext.Provider>
  );
}

Agencies.layout = (page) => {
  return (
    <AppLayout>
      <OrganizationLayout
        header="Global accounts"
        icon={
          <LayoutIcon>
            <IconGlobe size={50} className="text-black" />
          </LayoutIcon>
        }
        action={{
          label: "New global account",
          callback: () => Inertia.visit(route("admin.agencies.create")),
        }}
        byline={`${page.props?.organizations?.data?.length || 0} global accounts in total`}
      >
        {page}
      </OrganizationLayout>
    </AppLayout>
  );
};
